import { getLineItemItemId } from '@/lib/commerce/line-item-id';
import { PurchaseUseCase } from '../usecases/purchase.usecase';
import { AnalyticsUseCase } from '../usecases/analytics.usecase';
import { UserRepository } from '../repository/user.repository';
import { CreatePurchaseData, UpdatePurchaseData, GetPurchaseData, PurchaseFilter, GetPurchaseItemData } from '../repository/purchase.repository';

export class PurchaseService {
  constructor(
    private purchaseUseCase: PurchaseUseCase,
    private userRepository: UserRepository,
    private analyticsUseCase?: AnalyticsUseCase
  ) {
    console.log('[PurchaseService] Constructor - Purchase use case initialized:', !!this.purchaseUseCase);
  }

  // ===== RESOURCE PURCHASE FULFILLMENT =====

  /**
   * Fulfill resource purchase by updating user and resource documents
   * Called after a successful purchase to ensure proper data updates
   */
  private async fulfillResourcePurchase(purchase: GetPurchaseData): Promise<void> {
    console.log('[PurchaseService.fulfillResourcePurchase] START - Purchase:', purchase.id);
    
    try {
      // Check if purchase contains resources
      const resourceItems =
        purchase.items?.filter(
          (item) => String((item as { itemType?: string }).itemType || '').toLowerCase() === 'resource'
        ) || [];

      if (resourceItems.length === 0) {
        console.log('[PurchaseService.fulfillResourcePurchase] No resources in purchase, skipping fulfillment');
        return;
      }

      console.log('[PurchaseService.fulfillResourcePurchase] Found', resourceItems.length, 'resources to fulfill');

      // Get user information
      const user = await this.userRepository.getUserById(purchase.userId);
      if (!user) {
        throw new Error('User not found for purchase fulfillment');
      }

      // Prepare purchase items data
      const purchaseItems = resourceItems
        .map((item) => {
          const resourceId = getLineItemItemId(item);
          if (!resourceId) return null;
          return {
            resourceId,
            purchaseId: purchase.id,
            purchaseDate: purchase.createdAt?.toDate?.() || new Date(),
            amount: item.amount || item.price || 0,
            title: item.title || item.name,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row != null);

      if (purchaseItems.length === 0) {
        console.warn('[PurchaseService.fulfillResourcePurchase] No valid resource IDs found in purchase items');
        return;
      }

      // Update user document with purchased resources
      await this.updateUserPurchasedResources(purchase.userId, user, purchaseItems);

      // Update resource documents with buyer information
      await this.updateResourceBuyers(purchaseItems.map(item => item.resourceId), user, purchaseItems);

      console.log('[PurchaseService.fulfillResourcePurchase] SUCCESS - Fulfilled', purchaseItems.length, 'resources');

    } catch (error) {
      console.error('[PurchaseService.fulfillResourcePurchase] ERROR:', error);
      // Don't throw - we don't want to fail the purchase creation
      console.warn('[PurchaseService.fulfillResourcePurchase] Purchase fulfillment failed, but purchase is still valid');
    }
  }

  /**
   * Update user document to include purchased resources
   */
  private async updateUserPurchasedResources(
    userId: string,
    user: any,
    purchaseItems: Array<{ resourceId: string; purchaseId: string; purchaseDate: Date; amount: number; title?: string; }>
  ): Promise<void> {
    console.log('[PurchaseService.updateUserPurchasedResources] START - UserId:', userId, 'Items:', purchaseItems.length);
    
    try {
      // Get current purchased resources from user document
      const currentPurchasedResources = user.purchasedResources || [];
      
      // Create new resource entries
      const newResourceEntries = purchaseItems.map(item => ({
        resourceId: item.resourceId,
        purchaseId: item.purchaseId,
        purchasedAt: item.purchaseDate,
        amountPaid: item.amount,
        title: item.title,
        accessGranted: true,
        downloadCount: 0,
        lastAccessed: null
      }));

      // Merge with existing resources (avoid duplicates)
      const existingResourceIds = new Set(currentPurchasedResources.map((r: any) => r.resourceId));
      const uniqueNewEntries = newResourceEntries.filter(entry => !existingResourceIds.has(entry.resourceId));
      
      const updatedPurchasedResources = [...currentPurchasedResources, ...uniqueNewEntries];

      // Update user document
      await this.userRepository.updateUser(userId, {
        purchasedResources: updatedPurchasedResources,
        totalResourcePurchases: (user.totalResourcePurchases || 0) + uniqueNewEntries.length,
        lastPurchaseDate: new Date(),
        updatedAt: new Date()
      });

      console.log('[PurchaseService.updateUserPurchasedResources] SUCCESS - Added', uniqueNewEntries.length, 'new resources to user');

    } catch (error) {
      console.error('[PurchaseService.updateUserPurchasedResources] ERROR:', error);
      throw new Error('Failed to update user purchased resources');
    }
  }

  /**
   * Update resource documents to include buyer information
   */
  private async updateResourceBuyers(
    resourceIds: string[],
    user: any,
    purchaseItems: Array<{ resourceId: string; purchaseId: string; purchaseDate: Date; amount: number; title?: string; }>
  ): Promise<void> {
    console.log('[PurchaseService.updateResourceBuyers] START - ResourceIds:', resourceIds.length, 'User:', user.email);
    
    try {
      // Get ResourcesService to access resource repository
      const { default: services } = await import('@/lib/services');
      
      // Update each resource with buyer information
      for (const resourceId of resourceIds) {
        try {
          console.log('[PurchaseService.updateResourceBuyers] Updating resource:', resourceId);
          
          // Get current resource data
          const resource = await services.resources.getResourceById(resourceId);
          if (!resource) {
            console.warn('[PurchaseService.updateResourceBuyers] Resource not found:', resourceId);
            continue;
          }

          // Find purchase info for this resource
          const purchaseInfo = purchaseItems.find(item => item.resourceId === resourceId);
          if (!purchaseInfo) {
            console.warn('[PurchaseService.updateResourceBuyers] No purchase info found for resource:', resourceId);
            continue;
          }

          // Get current buyers list
          const currentBuyers = resource.buyers || [];
          
          // Check if user already exists in buyers list
          const existingBuyerIndex = currentBuyers.findIndex((buyer: any) => buyer.userId === user.id || buyer.userId === user.id);
          
          const buyerData = {
            userId: user.id || user.id,
            email: user.email,
            name: user.displayName || user.name || user.firstName + ' ' + user.lastName || 'Anonymous',
            purchaseId: purchaseInfo.purchaseId,
            purchasedAt: purchaseInfo.purchaseDate,
            amountPaid: purchaseInfo.amount,
            accessGranted: true,
            downloadCount: 0,
            lastAccessed: null
          };

          let updatedBuyers;
          if (existingBuyerIndex >= 0) {
            // Update existing buyer entry
            updatedBuyers = [...currentBuyers];
            updatedBuyers[existingBuyerIndex] = { ...updatedBuyers[existingBuyerIndex], ...buyerData };
          } else {
            // Add new buyer entry
            updatedBuyers = [...currentBuyers, buyerData];
          }

          // Update resource document
          await services.resources.updateResource(resourceId, {
            buyers: updatedBuyers,
            totalPurchases: (resource.totalPurchases || 0) + (existingBuyerIndex >= 0 ? 0 : 1),
            totalRevenue: (resource.totalRevenue || 0) + purchaseInfo.amount,
            lastPurchaseDate: purchaseInfo.purchaseDate,
            updatedAt: new Date()
          });

          console.log('[PurchaseService.updateResourceBuyers] SUCCESS - Updated resource:', resourceId);

        } catch (resourceError) {
          console.error('[PurchaseService.updateResourceBuyers] ERROR updating resource:', resourceId, resourceError);
          // Continue with other resources even if one fails
        }
      }

      console.log('[PurchaseService.updateResourceBuyers] SUCCESS - Updated all resources');

    } catch (error) {
      console.error('[PurchaseService.updateResourceBuyers] ERROR:', error);
      throw new Error('Failed to update resource buyers');
    }
  }

  /**
   * Get user's purchased resources from their user document
   * This provides quick access to what resources a user has purchased
   */
  async getUserPurchasedResources(userId: string): Promise<Array<{
    resourceId: string;
    purchaseId: string;
    purchasedAt: Date;
    amountPaid: number;
    title?: string;
    accessGranted: boolean;
    downloadCount: number;
    lastAccessed: Date | null;
  }>> {
    console.log('[PurchaseService.getUserPurchasedResources] START - UserId:', userId);
    
    try {
      const user = await this.userRepository.getUserById(userId);
      if (!user) {
        console.log('[PurchaseService.getUserPurchasedResources] User not found');
        return [];
      }

      const purchasedResources = user.purchasedResources || [];
      console.log('[PurchaseService.getUserPurchasedResources] SUCCESS - Found', purchasedResources.length, 'purchased resources');
      return purchasedResources;

    } catch (error) {
      console.error('[PurchaseService.getUserPurchasedResources] ERROR:', error);
      return [];
    }
  }

  /**
   * Check if user has access to a specific resource
   * Returns purchase details if user has purchased the resource
   */
  async checkUserResourceAccess(userId: string, resourceId: string): Promise<{
    hasAccess: boolean;
    purchaseDetails?: any;
  }> {
    console.log('[PurchaseService.checkUserResourceAccess] START - UserId:', userId, 'ResourceId:', resourceId);
    
    try {
      const purchasedResources = await this.getUserPurchasedResources(userId);
      const resourcePurchase = purchasedResources.find(r => r.resourceId === resourceId);
      
      if (resourcePurchase) {
        // If user has purchased the resource, they should have access regardless of accessGranted field
        // The accessGranted field might not be properly set in some older purchases
        console.log('[PurchaseService.checkUserResourceAccess] SUCCESS - User has purchased this resource');
        console.log('[PurchaseService.checkUserResourceAccess] Resource purchase details:', resourcePurchase);
        return {
          hasAccess: true,
          purchaseDetails: resourcePurchase
        };
      } else {
        console.log('[PurchaseService.checkUserResourceAccess] User has not purchased this resource');
        return { hasAccess: false };
      }

    } catch (error) {
      console.error('[PurchaseService.checkUserResourceAccess] ERROR:', error);
      return { hasAccess: false };
    }
  }

  // ===== PURCHASE MANAGEMENT =====

  async createPurchase(purchaseData: CreatePurchaseData): Promise<GetPurchaseData> {
    console.log('[PurchaseService.createPurchase] START - PurchaseData:', purchaseData);
    try {
      const purchase = await this.purchaseUseCase.createPurchase(purchaseData);

      // Fulfill resource purchases if this purchase contains resources
      await this.fulfillResourcePurchase(purchase);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'purchaseCreated',
          eventData: {
            purchaseId: purchase.id,
            userId: purchase.userId,
            totalAmount: purchase.totalAmount,
            currency: purchase.currency,
            payment_method: purchase.payment_method,
            payment_status: purchase.payment_status
          },
          userId: purchase.userId
        });
      }

      console.log('[PurchaseService.createPurchase] SUCCESS - Purchase created:', purchase.id);
      return purchase;
    } catch (error) {
      console.error('[PurchaseService.createPurchase] ERROR:', error);
      throw error;
    }
  }

  async createPurchaseFromCart(cartId: string, paymentData: {
    payment_method: 'stripe' | 'paypal' | 'apple_pay' | 'google_pay';
    paymentIntentId?: string;
    billingAddress?: Record<string, any>;
    shippingAddress?: Record<string, any>;
  }): Promise<GetPurchaseData> {
    console.log('[PurchaseService.createPurchaseFromCart] START - CartId:', cartId);
    try {
      const purchase = await this.purchaseUseCase.createPurchaseFromCart(cartId, paymentData);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'purchaseFromCart',
          eventData: {
            purchaseId: purchase.id,
            cartId: cartId,
            userId: purchase.userId,
            totalAmount: purchase.totalAmount,
            currency: purchase.currency,
            payment_method: purchase.payment_method
          },
          userId: purchase.userId
        });
      }

      console.log('[PurchaseService.createPurchaseFromCart] SUCCESS - Purchase created from cart');
      return purchase;
    } catch (error) {
      console.error('[PurchaseService.createPurchaseFromCart] ERROR:', error);
      throw error;
    }
  }

  async getPurchaseById(purchaseId: string): Promise<GetPurchaseData | null> {
    console.log('[PurchaseService.getPurchaseById] START - PurchaseId:', purchaseId);
    try {
      const purchase = await this.purchaseUseCase.getPurchaseById(purchaseId);
      console.log('[PurchaseService.getPurchaseById] SUCCESS - Purchase found:', !!purchase);
      return purchase;
    } catch (error) {
      console.error('[PurchaseService.getPurchaseById] ERROR:', error);
      throw error;
    }
  }

  async getPurchases(filter: PurchaseFilter = {}, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseService.getPurchases] START - Filter:', filter, 'Limit:', limitCount);
    try {
      const purchases = await this.purchaseUseCase.getPurchases(filter, limitCount);
      console.log('[PurchaseService.getPurchases] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseService.getPurchases] ERROR:', error);
      throw error;
    }
  }

  async getAllPurchases(limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseService.getAllPurchases] START - Limit:', limitCount);
    try {
      const purchases = await this.purchaseUseCase.getAllPurchases(limitCount);
      console.log('[PurchaseService.getAllPurchases] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseService.getAllPurchases] ERROR:', error);
      throw error;
    }
  }

  async updatePurchase(purchaseId: string, updateData: UpdatePurchaseData): Promise<GetPurchaseData> {
    console.log('[PurchaseService.updatePurchase] START - PurchaseId:', purchaseId, 'UpdateData:', updateData);
    try {
      const updatedPurchase = await this.purchaseUseCase.updatePurchase(purchaseId, updateData);

      // Track analytics event for status changes
      if (this.analyticsUseCase && updateData.payment_status) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'purchaseStatusUpdated',
          eventData: {
            purchaseId: purchaseId,
            newStatus: updateData.payment_status,
            totalAmount: updatedPurchase.totalAmount
          },
          userId: updatedPurchase.userId
        });
      }

      console.log('[PurchaseService.updatePurchase] SUCCESS - Purchase updated');
      return updatedPurchase;
    } catch (error) {
      console.error('[PurchaseService.updatePurchase] ERROR:', error);
      throw error;
    }
  }

  async deletePurchase(purchaseId: string): Promise<void> {
    console.log('[PurchaseService.deletePurchase] START - PurchaseId:', purchaseId);
    try {
      // Get purchase info before deletion for analytics
      const purchase = await this.purchaseUseCase.getPurchaseById(purchaseId);
      
      await this.purchaseUseCase.deletePurchase(purchaseId);

      // Track analytics event
      if (this.analyticsUseCase && purchase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'purchaseDeleted',
          eventData: {
            purchaseId: purchaseId,
            totalAmount: purchase.totalAmount,
            payment_status: purchase.payment_status
          },
          userId: purchase.userId
        });
      }

      console.log('[PurchaseService.deletePurchase] SUCCESS - Purchase deleted');
    } catch (error) {
      console.error('[PurchaseService.deletePurchase] ERROR:', error);
      throw error;
    }
  }

  // ===== USER OPERATIONS =====

  async getUserPurchases(userId: string, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('🎯 [PurchaseService.getUserPurchases] START - UserId:', userId, 'Limit:', limitCount);
    try {
      console.log('🎯 [PurchaseService.getUserPurchases] Calling purchaseUseCase.getUserPurchases...');
      const purchases = await this.purchaseUseCase.getUserPurchases(userId, limitCount);
      console.log('🎯 [PurchaseService.getUserPurchases] SUCCESS - Retrieved', purchases.length, 'user purchases');
      return purchases;
    } catch (error) {
      console.error('🎯 [PurchaseService.getUserPurchases] ERROR:', error);
      throw error;
    }
  }

  async getUserPurchaseHistory(userId: string, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseService.getUserPurchaseHistory] START - UserId:', userId);
    try {
      const purchases = await this.purchaseUseCase.getUserPurchaseHistory(userId, limitCount);
      console.log('[PurchaseService.getUserPurchaseHistory] SUCCESS - Retrieved purchase history');
      return purchases;
    } catch (error) {
      console.error('[PurchaseService.getUserPurchaseHistory] ERROR:', error);
      throw error;
    }
  }

  async getUserPurchaseSummary(userId: string): Promise<{
    totalSpent: number;
    totalPurchases: number;
    averageOrderValue: number;
    lastPurchaseDate?: Date;
    favoritePaymentMethod?: string;
  }> {
    console.log('[PurchaseService.getUserPurchaseSummary] START - UserId:', userId);
    try {
      const purchases = await this.purchaseUseCase.getUserPurchaseHistory(userId);
      const metrics = await this.purchaseUseCase.getPurchaseMetrics(userId);

      // Calculate favorite payment method
      const paymentMethods = purchases.map(p => p.payment_method);
      const methodCounts = paymentMethods.reduce((acc, method) => {
        acc[method] = (acc[method] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const favoritePaymentMethod = Object.entries(methodCounts)
        .sort(([,a], [,b]) => b - a)[0]?.[0];

      const summary = {
        ...metrics,
        favoritePaymentMethod
      };

      console.log('[PurchaseService.getUserPurchaseSummary] SUCCESS - Summary calculated');
      return summary;
    } catch (error) {
      console.error('[PurchaseService.getUserPurchaseSummary] ERROR:', error);
      throw error;
    }
  }

  // ===== PAYMENT STATUS OPERATIONS =====

  async updatePaymentStatus(purchaseId: string, status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded'): Promise<GetPurchaseData> {
    console.log('[PurchaseService.updatePaymentStatus] START - PurchaseId:', purchaseId, 'Status:', status);
    try {
      const updatedPurchase = await this.purchaseUseCase.updatePaymentStatus(purchaseId, status);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'paymentStatusChanged',
          eventData: {
            purchaseId: purchaseId,
            status: status,
            totalAmount: updatedPurchase.totalAmount,
            payment_method: updatedPurchase.payment_method
          },
          userId: updatedPurchase.userId
        });
      }

      console.log('[PurchaseService.updatePaymentStatus] SUCCESS - Payment status updated');
      return updatedPurchase;
    } catch (error) {
      console.error('[PurchaseService.updatePaymentStatus] ERROR:', error);
      throw error;
    }
  }

  async completePurchase(purchaseId: string, paymentIntentId?: string): Promise<GetPurchaseData> {
    console.log('[PurchaseService.completePurchase] START - PurchaseId:', purchaseId);
    try {
      const completedPurchase = await this.purchaseUseCase.completePurchase(purchaseId, paymentIntentId);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'purchaseCompleted',
          eventData: {
            purchaseId: purchaseId,
            totalAmount: completedPurchase.totalAmount,
            currency: completedPurchase.currency,
            payment_method: completedPurchase.payment_method,
            completionTime: new Date().toISOString()
          },
          userId: completedPurchase.userId
        });
      }

      console.log('[PurchaseService.completePurchase] SUCCESS - Purchase completed');
      return completedPurchase;
    } catch (error) {
      console.error('[PurchaseService.completePurchase] ERROR:', error);
      throw error;
    }
  }

  async refundPurchase(purchaseId: string, reason?: string): Promise<GetPurchaseData> {
    console.log('[PurchaseService.refundPurchase] START - PurchaseId:', purchaseId, 'Reason:', reason);
    try {
      const refundedPurchase = await this.purchaseUseCase.refundPurchase(purchaseId, reason);

      // Track analytics event
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent({
          eventType: 'purchaseRefunded',
          eventData: {
            purchaseId: purchaseId,
            totalAmount: refundedPurchase.totalAmount,
            currency: refundedPurchase.currency,
            refundReason: reason,
            refundTime: new Date().toISOString()
          },
          userId: refundedPurchase.userId
        });
      }

      console.log('[PurchaseService.refundPurchase] SUCCESS - Purchase refunded');
      return refundedPurchase;
    } catch (error) {
      console.error('[PurchaseService.refundPurchase] ERROR:', error);
      throw error;
    }
  }

  async getPurchasesByStatus(status: PurchaseFilter['payment_status'], limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseService.getPurchasesByStatus] START - Status:', status, 'Limit:', limitCount);
    try {
      const purchases = await this.purchaseUseCase.getPurchasesByStatus(status, limitCount);
      console.log('[PurchaseService.getPurchasesByStatus] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseService.getPurchasesByStatus] ERROR:', error);
      throw error;
    }
  }

  async getPendingPurchases(limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseService.getPendingPurchases] START - Limit:', limitCount);
    try {
      return await this.getPurchasesByStatus('pending', limitCount);
    } catch (error) {
      console.error('[PurchaseService.getPendingPurchases] ERROR:', error);
      throw error;
    }
  }

  async getFailedPurchases(limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseService.getFailedPurchases] START - Limit:', limitCount);
    try {
      return await this.getPurchasesByStatus('failed', limitCount);
    } catch (error) {
      console.error('[PurchaseService.getFailedPurchases] ERROR:', error);
      throw error;
    }
  }

  // ===== PURCHASE ITEMS =====

  async getPurchaseItems(purchaseId: string): Promise<GetPurchaseItemData[]> {
    console.log('[PurchaseService.getPurchaseItems] START - PurchaseId:', purchaseId);
    try {
      const items = await this.purchaseUseCase.getPurchaseItems(purchaseId);
      console.log('[PurchaseService.getPurchaseItems] SUCCESS - Retrieved', items.length, 'items');
      return items;
    } catch (error) {
      console.error('[PurchaseService.getPurchaseItems] ERROR:', error);
      throw error;
    }
  }

  async getPurchaseWithItems(purchaseId: string): Promise<{ purchase: GetPurchaseData; items: GetPurchaseItemData[] } | null> {
    console.log('[PurchaseService.getPurchaseWithItems] START - PurchaseId:', purchaseId);
    try {
      const purchase = await this.purchaseUseCase.getPurchaseById(purchaseId);
      if (!purchase) {
        console.log('[PurchaseService.getPurchaseWithItems] Purchase not found');
        return null;
      }

      const items = await this.purchaseUseCase.getPurchaseItems(purchaseId);
      
      console.log('[PurchaseService.getPurchaseWithItems] SUCCESS - Purchase with', items.length, 'items');
      return { purchase, items };
    } catch (error) {
      console.error('[PurchaseService.getPurchaseWithItems] ERROR:', error);
      throw error;
    }
  }

  // ===== ANALYTICS AND REPORTING =====

  async getRevenueAnalytics(startDate?: Date, endDate?: Date) {
    console.log('[PurchaseService.getRevenueAnalytics] START - DateRange:', startDate, 'to', endDate);
    try {
      const analytics = await this.purchaseUseCase.getRevenueAnalytics(startDate, endDate);
      console.log('[PurchaseService.getRevenueAnalytics] SUCCESS - Analytics calculated');
      return analytics;
    } catch (error) {
      console.error('[PurchaseService.getRevenueAnalytics] ERROR:', error);
      throw error;
    }
  }

  async getPurchaseTrends(days: number = 30): Promise<{
    daily: Array<{ date: string; revenue: number; transactions: number }>;
    totalRevenue: number;
    totalTransactions: number;
    averageDailyRevenue: number;
    growthRate: number;
  }> {
    console.log('[PurchaseService.getPurchaseTrends] START - Days:', days);
    try {
      const endDate = new Date();
      const startDate = new Date(endDate.getTime() - (days * 24 * 60 * 60 * 1000));

      const purchases = await this.purchaseUseCase.getPurchases({
        payment_status: 'completed',
        createdAfter: startDate,
        createdBefore: endDate
      });

      // Group by day
      const dailyData = new Map<string, { revenue: number; transactions: number }>();
      
      for (const purchase of purchases) {
        const dateKey = purchase.createdAt.toDate().toISOString().split('T')[0];
        const existing = dailyData.get(dateKey) || { revenue: 0, transactions: 0 };
        dailyData.set(dateKey, {
          revenue: existing.revenue + purchase.totalAmount,
          transactions: existing.transactions + 1
        });
      }

      const daily = Array.from(dailyData.entries()).map(([date, data]) => ({
        date,
        ...data
      })).sort((a, b) => a.date.localeCompare(b.date));

      const totalRevenue = purchases.reduce((sum, p) => sum + p.totalAmount, 0);
      const totalTransactions = purchases.length;
      const averageDailyRevenue = totalRevenue / days;

      // Calculate growth rate (simplified - comparing first and last weeks)
      const firstWeekRevenue = daily.slice(0, 7).reduce((sum, d) => sum + d.revenue, 0);
      const lastWeekRevenue = daily.slice(-7).reduce((sum, d) => sum + d.revenue, 0);
      const growthRate = firstWeekRevenue > 0 ? ((lastWeekRevenue - firstWeekRevenue) / firstWeekRevenue) * 100 : 0;

      const trends = {
        daily,
        totalRevenue,
        totalTransactions,
        averageDailyRevenue,
        growthRate
      };

      console.log('[PurchaseService.getPurchaseTrends] SUCCESS - Trends calculated');
      return trends;
    } catch (error) {
      console.error('[PurchaseService.getPurchaseTrends] ERROR:', error);
      throw error;
    }
  }

  async getPaymentMethodAnalytics(): Promise<Array<{ method: string; count: number; revenue: number; percentage: number }>> {
    console.log('[PurchaseService.getPaymentMethodAnalytics] START');
    try {
      const purchases = await this.purchaseUseCase.getPurchases({ payment_status: 'completed' });
      
      const methodStats = purchases.reduce((acc, purchase) => {
        const method = purchase.payment_method;
        if (!acc[method]) {
          acc[method] = { count: 0, revenue: 0 };
        }
        acc[method].count++;
        acc[method].revenue += purchase.totalAmount;
        return acc;
      }, {} as Record<string, { count: number; revenue: number }>);

      const totalRevenue = purchases.reduce((sum, p) => sum + p.totalAmount, 0);

      const analytics = Object.entries(methodStats).map(([method, stats]) => ({
        method,
        count: stats.count,
        revenue: stats.revenue,
        percentage: totalRevenue > 0 ? (stats.revenue / totalRevenue) * 100 : 0
      })).sort((a, b) => b.revenue - a.revenue);

      console.log('[PurchaseService.getPaymentMethodAnalytics] SUCCESS - Analytics calculated');
      return analytics;
    } catch (error) {
      console.error('[PurchaseService.getPaymentMethodAnalytics] ERROR:', error);
      throw error;
    }
  }
}